/**
 * Custom component runtime (ADR-0022 · open registries #2).
 *
 * A page `custom_component` runs author-supplied React in a **sandboxed iframe**
 * (`sandbox="allow-scripts"`, NO `allow-same-origin` → opaque origin: it cannot
 * read the parent DOM, cookies, localStorage, or the portal JWT). The portal
 * serves UNTRUSTED end-users (audience ≠ author), so the sandbox is mandatory.
 *
 * The frame talks to the page only through a `postMessage` protocol — a two-way
 * `useTrayModel()` binding:
 *   - parent → frame: `tray:init` (compiled code / bundle url + first model),
 *     then `tray:model` on every page-state change;
 *   - frame → parent: `tray:hello` (ready for init), `tray:action`
 *     (setVar / refresh / navigate / toast — the SAME surface a button has), and
 *     `tray:error`.
 *
 * Two AUTHORING MODES (ADR-0046, the `mode` of an `AppCustomComponent`):
 *   - **react** (default) — the author writes JSX/TS; we compile it WITH SUCRASE
 *     **in the parent** (a lazy chunk, never shipped into the frame) and post the
 *     plain-JS result; the frame `new Function`-evals it with React + a `require`
 *     shim in scope. (Or a prebuilt ES module URL via the `bundle` source, which
 *     the frame `import()`s — cross-origin → the host must send CORS.)
 *   - **html** — HTML/CSS/JS: the author writes markup + CSS + JS (stored as a
 *     JSON `{html,css,js}` blob). The frame injects the CSS into a `<style>`, the
 *     HTML into a mount element, then runs the JS with that mount as `root` plus
 *     the `tray` bridge (model + actions + `onModel`) and the app's libraries.
 *     No React, no JSX transform.
 *
 * EXTERNAL LIBRARIES (ADR-0046): the app's pinned libraries are passed as
 * descriptors and the FRAME loads each from its CDN url (the opaque origin can't
 * reuse the parent's globals), exposing `window[global]` + `require(name)` — in
 * BOTH modes — before running author code.
 *
 * React itself is served **same-origin** from `/custom-frame/*` (vendored UMD) so
 * the opaque-origin frame can load it by absolute URL — no external CDN, fully
 * self-hosted/offline. See `public/custom-frame/README.md`.
 */
import type { PageRuntime } from "@/components/portal/PageRuntime";
import type { JsLibraryOut } from "@/lib/api-apps";
import { type Scope, renderTemplate } from "@/lib/pageExpr";

/** A library DESCRIPTOR passed into the frame (ADR-0046). The frame itself loads
 *  the bundle from the pinned CDN url (it has an opaque origin and can't reuse
 *  the parent's already-loaded globals), then exposes `window[global]` + makes
 *  `require(name)` resolve to it. We pass only the metadata, never bytes. */
export interface FrameLibrary {
  name: string;
  global: string;
  url: string;
  format: "umd" | "esm";
}

/** Project the app's pinned libraries to the frame descriptors, dropping any
 *  malformed entry (a bad library must never break the frame). */
export function frameLibraries(libs: JsLibraryOut[] | undefined): FrameLibrary[] {
  return (libs ?? [])
    .filter((l) => l && l.name && l.global && l.url)
    .map((l) => ({
      name: l.name,
      global: l.global,
      url: l.url,
      format: l.format === "esm" ? "esm" : "umd",
    }));
}

/** JSON-safe snapshot of the page state handed to the frame. NEVER includes the
 *  JWT or any credential — only the whitelisted, already-public page surface. */
export interface TrayModelSnapshot {
  vars: Record<string, unknown>;
  queries: Record<string, unknown>;
  user: { id: string; email: string; full_name?: string | null } | null;
  role: { id: string; slug: string; name: string } | null;
  appSlug: string;
  pageSlug: string;
}

/** Messages the FRAME sends to the parent. */
export type FrameMessage =
  | { type: "tray:hello" }
  | { type: "tray:error"; message: string }
  | {
      type: "tray:action";
      action: "setVar" | "refresh" | "navigate" | "toast";
      args: unknown[];
    };

// Same-origin path to the vendored UMD assets. MUST respect Vite's `base`
// ("/"): public/ assets are served under the base, so a root-absolute
// "/custom-frame" 404s in the real app (the opaque-origin frame then has no
// React → `ReactDOM.createRoot` throws). `import.meta.env.BASE_URL` is "/"
// in the app and "/" in tests.
export const FRAME_ASSET_BASE = `${import.meta.env.BASE_URL}custom-frame`.replace(
  /\/{2,}/g,
  "/",
);

/** Build the whitelisted model snapshot from the live runtime. Explicit field
 *  picking is the security boundary — do NOT spread `rt` (it would leak methods
 *  and, worse, any token the runtime might hold). */
export function serializeModel(rt: PageRuntime): TrayModelSnapshot {
  return {
    vars: jsonSafe(rt.vars) as Record<string, unknown>,
    queries: jsonSafe(rt.queries) as Record<string, unknown>,
    user: rt.user
      ? { id: rt.user.id, email: rt.user.email, full_name: rt.user.full_name ?? null }
      : null,
    role: rt.role ? { id: rt.role.id, slug: rt.role.slug, name: rt.role.name } : null,
    appSlug: rt.appSlug,
    pageSlug: rt.pageSlug,
  };
}

/** Build the model snapshot from a builder SCOPE (no live runtime). Used for the
 *  builder canvas preview, where `$queries`/`$page` carry sample data and the
 *  actions are inert. Mirrors `serializeModel`'s whitelist. */
export function modelFromScope(scope: Scope): TrayModelSnapshot {
  const s = scope as Record<string, unknown>;
  const u = s.$user as { id?: unknown; email?: unknown; full_name?: unknown } | null | undefined;
  const r = s.$role as { id?: unknown; slug?: unknown; name?: unknown } | null | undefined;
  return {
    vars: (jsonSafe(s.$page) as Record<string, unknown>) ?? {},
    queries: (jsonSafe(s.$queries) as Record<string, unknown>) ?? {},
    user: u ? { id: String(u.id ?? ""), email: String(u.email ?? ""), full_name: (u.full_name as string | null) ?? null } : null,
    role: r ? { id: String(r.id ?? ""), slug: String(r.slug ?? ""), name: String(r.name ?? "") } : null,
    appSlug: "",
    pageSlug: "",
  };
}

/** The three sections of an HTML/CSS/JS custom component (ADR-0046). A
 *  `mode="html"` component's `code` column holds these as a JSON object so the
 *  editor can split them into tabs and the frame can render markup + style +
 *  behaviour separately. */
export interface HtmlParts {
  html: string;
  css: string;
  js: string;
}

/** Split a stored html-mode `code` into its three sections. A JSON
 *  `{html,css,js}` blob round-trips; anything else (e.g. a legacy plain-JS
 *  body) is treated as the JS section so nothing is lost. */
export function htmlPartsFromCode(code: string): HtmlParts {
  try {
    const p = JSON.parse(code) as Record<string, unknown>;
    if (p && typeof p === "object" && !Array.isArray(p) && ("html" in p || "css" in p || "js" in p)) {
      return {
        html: typeof p.html === "string" ? p.html : "",
        css: typeof p.css === "string" ? p.css : "",
        js: typeof p.js === "string" ? p.js : "",
      };
    }
  } catch {
    // not JSON → a legacy plain-JS body
  }
  return { html: "", css: "", js: code };
}

/** Serialise the three sections back into the stored `code` column. */
export function codeFromHtmlParts(parts: HtmlParts): string {
  return JSON.stringify({ html: parts.html, css: parts.css, js: parts.js });
}

/** Best-effort JSON round-trip so a non-serialisable value can't crash
 *  `postMessage`'s structured clone (functions, symbols, cycles → dropped). */
function jsonSafe(value: unknown): unknown {
  try {
    return JSON.parse(JSON.stringify(value ?? null));
  } catch {
    return null;
  }
}

/** Render `props_json` (a `{{ }}`-templated JSON object string) against the page
 *  scope into a plain props object. Malformed → `{}` (never throws into render). */
export function parseProps(propsJson: unknown, scope: Scope): Record<string, unknown> {
  if (typeof propsJson !== "string" || !propsJson.trim()) return {};
  let rendered = propsJson;
  try {
    if (propsJson.includes("{{")) rendered = String(renderTemplate(propsJson, scope));
  } catch {
    return {};
  }
  try {
    const parsed = JSON.parse(rendered);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

/** Compile author JSX/TS → plain JS (CJS) using sucrase, loaded lazily so it
 *  never weighs down the main bundle. `imports` makes `import`/`export default`
 *  run inside our `new Function` + `require` shim. */
export async function transpile(code: string): Promise<{ code: string } | { error: string }> {
  try {
    const { transform } = await import("sucrase");
    const out = transform(code, {
      transforms: ["jsx", "typescript", "imports"],
      production: true,
    });
    return { code: out.code };
  } catch (e) {
    return { error: (e as Error)?.message ?? "transpile failed" };
  }
}

/**
 * The plain-ES bootstrap that runs INSIDE the frame. It defines `useTrayModel`,
 * the message bridge, and the eval/import loaders. Kept as a string (injected
 * into `srcdoc`) — it is NOT app code and runs in the sandbox, not our origin.
 * No `</script>` literal may appear here (it would close the inline tag).
 */
const FRAME_BOOTSTRAP = `(function () {
  var PARENT = window.parent;
  var nonce = null;
  var model = {};
  var lastProps = {};
  var listeners = [];
  var LIBS = {};               // package NAME -> loaded value (require() lookup)
  var librariesLoaded = false; // load the app's libraries exactly once
  var componentMode = "react"; // "react" (JSX) | "html" (vanilla DOM)

  function post(msg) { try { PARENT.postMessage(msg, "*"); } catch (e) {} }
  function notify() { listeners.slice().forEach(function (l) { try { l(model); } catch (e) {} }); }

  // The \`tray\` bridge — the page surface available to author code in BOTH
  // modes: the whitelisted \`model\` snapshot + the governed actions (the SAME
  // surface a button has) + \`onModel\` for reactive updates. React authors get
  // it via \`useTrayModel()\`; HTML authors get it as the \`tray\` argument.
  var tray = {
    model: model,
    setVar: function (k, v) { post({ type: "tray:action", action: "setVar", args: [String(k), v] }); },
    refresh: function () { post({ type: "tray:action", action: "refresh", args: [] }); },
    navigate: function (to) { post({ type: "tray:action", action: "navigate", args: [String(to)] }); },
    toast: function (m) { post({ type: "tray:action", action: "toast", args: [String(m)] }); },
    onModel: function (cb) {
      if (typeof cb !== "function") return function () {};
      listeners.push(cb);
      try { cb(model); } catch (e) {}
      return function () { var i = listeners.indexOf(cb); if (i >= 0) listeners.splice(i, 1); };
    }
  };
  window.tray = tray;

  // DX: expose the common React hooks (+ createElement/Fragment) as frame
  // globals so an author can write \`useState(0)\` directly, without an
  // \`import { useState } from 'react'\`. (Named imports + \`React.useState\`
  // still work via the require shim.)
  (function () {
    var R = window.React;
    if (!R) return;
    ["useState","useEffect","useMemo","useCallback","useRef","useReducer",
     "useContext","useLayoutEffect","createElement","Fragment"].forEach(function (k) {
      if (R[k] !== undefined && window[k] === undefined) window[k] = R[k];
    });
  })();

  // React module shim: works for both \`import React from 'react'\` (interop
  // picks .default) and named imports (\`{ useState }\`).
  function reactModule() {
    var R = window.React;
    return Object.assign({ default: R, __esModule: true }, R);
  }
  function reactDomModule() {
    var RD = window.ReactDOM;
    return Object.assign({ default: RD, __esModule: true }, RD);
  }
  function req(name) {
    if (name === "react") return reactModule();
    if (name === "react-dom" || name === "react-dom/client") return reactDomModule();
    if (Object.prototype.hasOwnProperty.call(LIBS, name)) return LIBS[name];
    throw new Error("import not allowed in a custom component: " + name);
  }

  // ADR-0046: load each app library from its pinned CDN url INSIDE the frame
  // (the opaque origin can't reuse the parent's globals). UMD → a <script src>
  // read off window[global]; ESM → import(). The loaded value is exposed as
  // window[global] AND resolvable via require(name). A failed library is
  // SKIPPED (its global stays undefined) — never blocks the component.
  function loadOne(lib) {
    return new Promise(function (resolve) {
      function done(val) {
        if (val != null) {
          LIBS[lib.name] = val;
          if (lib.global && window[lib.global] === undefined) window[lib.global] = val;
        }
        resolve();
      }
      try {
        if (lib.format === "esm") {
          import(lib.url).then(function (m) { done(m && (m.default || m)); }, function () { resolve(); });
          return;
        }
        var s = document.createElement("script");
        s.src = lib.url; s.async = false;
        s.addEventListener("load", function () { done(window[lib.global]); }, { once: true });
        s.addEventListener("error", function () { resolve(); }, { once: true });
        document.head.appendChild(s);
      } catch (e) { resolve(); }
    });
  }
  function loadLibraries(list, cb) {
    if (librariesLoaded || !list || !list.length) { librariesLoaded = true; cb(); return; }
    Promise.all(list.map(loadOne)).then(function () { librariesLoaded = true; cb(); }, function () { librariesLoaded = true; cb(); });
  }

  function useTrayModel() {
    var R = window.React;
    var st = R.useState(model);
    var setState = st[1];
    R.useEffect(function () {
      var l = function (m) { setState(m); };
      listeners.push(l);
      return function () { var i = listeners.indexOf(l); if (i >= 0) listeners.splice(i, 1); };
    }, []);
    return Object.assign({}, st[0], {
      setVar: tray.setVar, refresh: tray.refresh, navigate: tray.navigate, toast: tray.toast
    });
  }
  window.useTrayModel = useTrayModel;

  // Targeted, friendlier hints for the most common authoring mistakes — appended
  // under the raw error so the cause is obvious without decoding React codes.
  function errorHint(msg) {
    var s = String(msg);
    if (/Objects are not valid as a React child/i.test(s) || /error #31\\b/.test(s)) {
      return "Hint: rendering an object in JSX. Inside the component use a SINGLE brace — {value} — not {{value}} (double braces make an object literal). The {{ }} template syntax is only for page FIELDS, not component code.";
    }
    if (/is not defined\\b/.test(s)) {
      return "Hint: an identifier isn't in scope. React hooks (useState, useEffect, …) are available as globals; for anything else import it or pass it via props.";
    }
    if (/Invalid hook call/i.test(s) || /error #321\\b/.test(s)) {
      return "Hint: hooks (useState, …) can only be called at the top level of the Component function — not inside loops, conditions, or nested callbacks.";
    }
    return "";
  }

  function renderError(msg) {
    // Drop the React root: renderError writes raw DOM into #root, so a reused
    // root would point at detached nodes. Next mount recreates it cleanly.
    _root = null;
    var el = document.getElementById("root");
    if (el) {
      el.textContent = "";
      var pre = document.createElement("pre");
      pre.style.cssText = "color:#e11d48;font:12px/1.5 ui-monospace,SFMono-Regular,Menlo,monospace;white-space:pre-wrap;margin:0;padding:8px";
      pre.textContent = String(msg);
      el.appendChild(pre);
      var hint = errorHint(msg);
      if (hint) {
        var h = document.createElement("pre");
        h.style.cssText = "color:#9a6700;background:#fff8e1;font:12px/1.5 ui-monospace,SFMono-Regular,Menlo,monospace;white-space:pre-wrap;margin:8px;padding:8px;border-radius:6px";
        h.textContent = hint;
        el.appendChild(h);
      }
    }
    post({ type: "tray:error", message: String(msg) });
  }

  // Class error boundary so a RENDER-time throw (e.g. a hook used wrong, an
  // undefined reference inside JSX) shows inline instead of leaving the frame
  // blank. React does NOT route render errors through mountComponent's
  // try/catch (render is its own phase), so a boundary is the only way.
  function errorBoundary(R) {
    function EB(p) { R.Component.call(this, p); this.state = { err: null }; }
    EB.prototype = Object.create(R.Component.prototype);
    EB.prototype.constructor = EB;
    EB.getDerivedStateFromError = function (e) { return { err: (e && e.message) || String(e) }; };
    EB.prototype.componentDidCatch = function (e) {
      post({ type: "tray:error", message: (e && e.message) || String(e) });
    };
    EB.prototype.render = function () {
      if (this.state && this.state.err) {
        return R.createElement("pre", {
          style: { color: "#e11d48", font: "12px/1.5 ui-monospace,SFMono-Regular,Menlo,monospace", whiteSpace: "pre-wrap", margin: 0, padding: 8 },
        }, this.state.err);
      }
      return this.props.children;
    };
    return EB;
  }

  var _root = null;
  function mountComponent(Component, props) {
    try {
      var R = window.React, RD = window.ReactDOM;
      function Wrapper() {
        var m = useTrayModel();
        return R.createElement(Component, Object.assign({ model: m }, props));
      }
      // Reuse one root across re-renders (tray:code on every edit) — calling
      // createRoot twice on the same node warns + leaks.
      if (!_root) _root = RD.createRoot(document.getElementById("root"));
      _root.render(R.createElement(errorBoundary(R), null, R.createElement(Wrapper)));
    } catch (e) { renderError((e && e.message) || e); }
  }

  function loadFromCode(code, props) {
    try {
      var module = { exports: {} };
      var tail = "\\n;try{ if (typeof Component!=='undefined' && !module.exports.default && !module.exports.Component) module.exports.Component = Component; }catch(_e){}";
      var fn = new Function("module", "exports", "require", "React", "ReactDOM", "useTrayModel", code + tail);
      fn(module, module.exports, req, window.React, window.ReactDOM, useTrayModel);
      var C = module.exports.default || module.exports.Component;
      if (!C) { renderError("No component exported. Define function Component({ model }) {…} or export default."); return; }
      mountComponent(C, props);
    } catch (e) { renderError((e && e.message) || e); }
  }

  function loadFromBundle(url, props) {
    try {
      import(url).then(function (mod) {
        var C = mod && (mod.default || mod.Component);
        if (!C) { renderError("Bundle exported no Component / default."); return; }
        mountComponent(C, props);
      }).catch(function (e) { renderError("Failed to load bundle: " + ((e && e.message) || e)); });
    } catch (e) { renderError((e && e.message) || e); }
  }

  // HTML/CSS/JS mode (ADR-0046): the author writes HTML markup + CSS + JS. The
  // stored \`code\` is a JSON {html,css,js} blob; we inject the CSS into a <style>,
  // the HTML into a mount div, then run the JS with that mount as \`root\` plus
  // \`tray\` (model + actions + onModel) and \`require\` / the library globals.
  // A non-JSON \`code\` (legacy) is treated as a plain-JS body into \`root\`.
  function parseHtmlParts(code) {
    try {
      var p = JSON.parse(code);
      if (p && typeof p === "object" && !(p instanceof Array) &&
          ("html" in p || "css" in p || "js" in p)) {
        return { html: String(p.html || ""), css: String(p.css || ""), js: String(p.js || "") };
      }
    } catch (e) {}
    return null;
  }
  function loadHtmlComponent(code) {
    try {
      _root = null; // drop any React root from a prior react-mode render
      var el = document.getElementById("root");
      if (!el) return;
      el.textContent = "";
      var parts = parseHtmlParts(code);
      if (parts) {
        if (parts.css) {
          var style = document.createElement("style");
          style.textContent = parts.css;
          el.appendChild(style);
        }
        var mount = document.createElement("div");
        mount.innerHTML = parts.html;
        el.appendChild(mount);
        if (parts.js) {
          var fn = new Function("root", "tray", "require", "props", parts.js);
          fn(mount, tray, req, lastProps);
        }
      } else {
        // legacy: the whole code is a plain-JS body into the root element.
        var fn2 = new Function("root", "tray", "require", "props", code);
        fn2(el, tray, req, lastProps);
      }
    } catch (e) { renderError((e && e.message) || e); }
  }

  // Dispatch the inline-code path by the authoring mode (bundle is react-only).
  function run(code) {
    if (componentMode === "html") loadHtmlComponent(code);
    else loadFromCode(code, lastProps);
  }

  var inited = false;
  window.addEventListener("message", function (e) {
    if (e.source !== PARENT) return;
    var d = e.data;
    if (!d || typeof d !== "object") return;
    if (d.type === "tray:init") {
      if (inited) return; // ignore duplicate inits from the hello-retry handshake
      inited = true;
      nonce = d.nonce || null;
      model = d.model || {}; tray.model = model;
      lastProps = d.props || {};
      componentMode = d.componentMode === "html" ? "html" : "react";
      loadLibraries(d.libraries || [], function () {
        if (d.mode === "bundle") loadFromBundle(d.bundleUrl, lastProps);
        else run(d.code || "");
      });
    } else if (d.type === "tray:code") {
      // Re-render after init when the author code changes (editor live preview /
      // async-loaded library code). Without this the frame stays stuck on the
      // FIRST code it ever received.
      if (nonce && d.nonce !== nonce) return;
      if (d.props) lastProps = d.props;
      // The RESOLVED mode can change AFTER init: a library node whose code+mode
      // come from a context entry that loads a tick late flips react→html. The
      // frame set \`componentMode\` only on tray:init, so it kept running an html
      // {html,css,js} blob through the REACT host (new Function(blob)) → the \`:\`
      // of the JSON threw "Unexpected token ':'". Re-read it on every code push.
      componentMode = d.componentMode === "html" ? "html" : "react";
      // Libraries are loaded once (librariesLoaded guard); cb runs immediately.
      loadLibraries(d.libraries || [], function () {
        if (d.mode === "bundle") loadFromBundle(d.bundleUrl, lastProps);
        else run(d.code || "");
      });
    } else if (d.type === "tray:model") {
      if (nonce && d.nonce !== nonce) return;
      model = d.model || {}; tray.model = model;
      notify();
    }
  });

  // Announce readiness — and KEEP announcing until init arrives. The parent
  // attaches its message listener in a React effect; if our first hello fires
  // before that (the frame's inline scripts can run first, esp. once React is
  // cached), it's lost and the frame would sit blank forever. Re-pinging until
  // \`inited\` makes the handshake race-proof. The parent ignores duplicate
  // hellos (idempotent) and the frame ignores duplicate inits.
  function hello() { if (!inited) post({ type: "tray:hello" }); }
  hello();
  var pingTimer = setInterval(function () {
    if (inited) { clearInterval(pingTimer); return; }
    hello();
  }, 150);
  setTimeout(function () { clearInterval(pingTimer); }, 4000); // give up after ~4s
})();`;

/** Build the iframe `srcdoc`. `origin` (the parent's `window.location.origin`)
 *  is baked into the React `<script src>` because a `srcdoc` frame has an opaque
 *  origin and can't resolve relative URLs.
 *
 *  `dev` loads the **development** React build (full, readable error messages +
 *  component stacks + hook-rule warnings) — used on the AUTHORING surfaces
 *  (builder canvas + library editor preview) so a mistake shows a real message,
 *  not `Minified React error #31`. The live portal (untrusted end-users) keeps
 *  the small production build. */
export function buildFrameSrcDoc(origin: string, dev = false): string {
  const base = `${origin}${FRAME_ASSET_BASE}`;
  const react = dev ? "react.development.js" : "react.production.min.js";
  const reactDom = dev ? "react-dom.development.js" : "react-dom.production.min.js";
  return [
    "<!doctype html><html><head><meta charset=\"utf-8\">",
    `<script src="${base}/${react}"></script>`,
    `<script src="${base}/${reactDom}"></script>`,
    "<style>html,body{margin:0;padding:0;background:transparent;font:14px/1.5 system-ui,-apple-system,sans-serif;color:#0f172a}#root{padding:0}</style>",
    "</head><body><div id=\"root\"></div>",
    `<script>${FRAME_BOOTSTRAP}</script>`,
    "</body></html>",
  ].join("");
}

/** Narrow an untrusted `message` event payload to a `FrameMessage`. */
export function asFrameMessage(data: unknown): FrameMessage | null {
  if (!data || typeof data !== "object") return null;
  const d = data as Record<string, unknown>;
  if (d.type === "tray:hello") return { type: "tray:hello" };
  if (d.type === "tray:error") return { type: "tray:error", message: String(d.message ?? "") };
  if (
    d.type === "tray:action" &&
    (d.action === "setVar" || d.action === "refresh" || d.action === "navigate" || d.action === "toast")
  ) {
    return {
      type: "tray:action",
      action: d.action,
      args: Array.isArray(d.args) ? d.args : [],
    };
  }
  return null;
}
